import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Swords, MoveHorizontal, ChevronsUp, ChevronsDown } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";

const MEDAL = ["from-amber-200 to-amber-500 text-amber-950", "from-slate-100 to-slate-400 text-slate-800", "from-orange-200 to-orange-500 text-orange-950"];

export const RunBoard = ({ rows, testId, empty = "Noch keine Läufe – sei die oder der Erste!" }) => (
  <ol className="space-y-2" data-testid={testId}>
    {!rows?.length && <li className="text-sm text-white/60 rn-glass rounded-2xl p-4">{empty}</li>}
    {rows?.map((r, i) => (
      <li key={r.user.user_id} className={`rn-glass flex items-center gap-3 rounded-2xl px-3 py-2.5 ${i === 0 ? "ring-1 ring-amber-300/50" : ""}`} data-testid={`${testId}-row-${i}`}>
        <span className={`w-7 h-7 shrink-0 rounded-full grid place-items-center text-xs font-black ${i < 3 ? `bg-gradient-to-br ${MEDAL[i]}` : "bg-white/10 text-white/70"}`}>{i === 0 ? <Crown className="w-3.5 h-3.5" /> : i + 1}</span>
        <UserAvatar name={r.user.name} picture={r.user.picture} userId={r.user.user_id} size="w-9 h-9" text="text-xs" noStory />
        <span className="flex-1 min-w-0 truncate font-bold text-white">{r.user.name}</span>
        <span className="font-black text-lg tabular-nums rn-gold-text pr-1">{r.score.toLocaleString("de-DE")}</span>
      </li>
    ))}
  </ol>
);

const HOW = [[MoveHorizontal, "Wischen ← →", "Spur wechseln"], [ChevronsUp, "Wischen ↑", "Springen"], [ChevronsDown, "Wischen ↓", "Rollen"]];
export const HowTo = () => (
  <div className="grid grid-cols-3 gap-2" data-testid="runner-howto">
    {HOW.map(([I, a, b]) => (
      <div key={a} className="rn-glass rounded-2xl p-3 text-center">
        <I className="w-5 h-5 mx-auto text-pink-300" />
        <p className="mt-1.5 text-xs font-black text-white">{a}</p>
        <p className="text-[11px] text-white/60">{b}</p>
      </div>
    ))}
  </div>
);

export function RunDuelPanel() {
  const [d, setD] = useState(null);
  const [fid, setFid] = useState("");
  const navigate = useNavigate();
  useEffect(() => { api.get("/runner/duels").then(({ data }) => setD(data)).catch(() => setD({ invites: [], friends: [] })); }, []);
  const challenge = async () => {
    try { const { data } = await api.post("/runner/duel", { friend_id: fid }); navigate(`/herz-laeufer/duell/${data.id}`); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!d) return null;
  return (
    <div className="rn-glass rounded-3xl p-4 space-y-3 relative overflow-hidden" data-testid="runner-duel-panel">
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-fuchsia-500/30 blur-2xl" />
      <p className="relative font-display text-lg font-black flex items-center gap-2 text-white"><Swords className="w-5 h-5 text-pink-300" /> Live-Duell</p>
      <p className="relative text-xs text-white/70">Ihr lauft gleichzeitig dieselbe Strecke und seht live, wer weiter kommt. Sieg = +5 Punkte.</p>
      {d.invites.map((i) => (
        <div key={i.id} className="relative flex items-center gap-3 rounded-2xl bg-pink-500/20 ring-1 ring-pink-400/30 p-2.5" data-testid={`runner-invite-${i.id}`}>
          <UserAvatar name={i.opp.user?.name} picture={i.opp.user?.picture} size="w-9 h-9" noStory />
          <span className="flex-1 text-sm font-bold text-white">{i.opp.user?.name?.split(" ")[0]} fordert dich heraus!</span>
          <button onClick={() => navigate(`/herz-laeufer/duell/${i.id}`)} data-testid={`runner-invite-open-${i.id}`} className="h-9 px-4 rounded-full bg-pink-500 text-sm font-black text-white">Ansehen</button>
        </div>
      ))}
      {d.friends.length ? (
        <div className="relative flex gap-2">
          <select value={fid} onChange={(e) => setFid(e.target.value)} data-testid="runner-duel-friend" className="flex-1 min-w-0 h-11 rounded-full bg-white/10 ring-1 ring-white/15 px-4 text-sm font-bold text-white">
            <option value="" className="text-slate-900">Freund wählen …</option>
            {d.friends.map((f) => <option key={f.user_id} value={f.user_id} className="text-slate-900">{f.name}</option>)}
          </select>
          <button onClick={challenge} disabled={!fid} data-testid="runner-duel-start" className="h-11 px-5 rounded-full rn-btn font-black text-white disabled:opacity-50">Duell</button>
        </div>
      ) : <p className="relative text-xs text-white/60" data-testid="runner-duel-nofriends">Füge zuerst Freunde hinzu, um sie herauszufordern.</p>}
    </div>
  );
}
